import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AudienceSpec,
  ChecklistItem,
  ChecklistItemType,
  ChecklistResult,
  ChecklistRun,
  ChecklistRunList,
  ChecklistSchedule,
  ChecklistTemplate,
  ChecklistTemplateList,
  PersonPage,
} from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { cn, useToast } from '@up2d8/ui';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  CardHead,
  Checkbox,
  CardPad,
  DefinitionList,
  Drawer,
  Empty,
  Field,
  FilterSelect,
  Grid,
  IconBtn,
  Input,
  Note,
  PageHead,
  Row,
  SearchField,
  Sect,
  Select,
  Stack,
  Stats,
  TabPanel,
  Tabs,
  Toolbar,
  useAdminToast,
} from '../admin-kit';
import { AudiencePicker } from '../communication/audience-picker';
import { LoadError, Loading, useShortWhen } from '../werkvloer';
import { saveBlob } from '../../shared/download';
import { useSession } from '../../shared/session';
import { formatDate, formatDateTime } from '../../shared/format';
import {
  NEW_PLANNING,
  emptyItem,
  itemBody,
  planningBody,
  planningChanged,
  planningOf,
  type DraftItem,
  type PlanningDraft,
} from './checklist-drafts';
import { audienceIncomplete } from '../../shared/audience-preview';

/** In de volgorde van de mockup: afvinken, getal met grenzen, foto, tekst. */
const ITEM_TYPES: ChecklistItemType[] = ['check', 'number', 'photo', 'text'];
const SCHEDULES: ChecklistSchedule[] = ['manual', 'daily', 'weekdays', 'weekly'];

/** `active` = open en te laat, de standaard: wat nog moet gebeuren. */
type RunFilter = 'active' | 'open' | 'overdue' | 'completed' | 'all';
type Period = 'all' | 'today' | 'week';

/**
 * Checklists in het beheer (Stap 38; herontworpen in BHR-6, frames W4 en W5).
 *
 * Twee delen, en ze horen bij twee mensen: de **rondes** (wat open staat, wat te laat is, waar een
 * afwijking zat) en de **sjablonen** (wat er afgevinkt moet worden, door wie en wanneer). Het
 * eerste staat achter een eigen recht, zodat een teamleider het mag zien zonder sjablonen te
 * kunnen wijzigen; wie `tasks.checklist.manage` niet heeft, ziet de tab Sjablonen niet.
 */
export function ChecklistsAdminPage() {
  const { t } = useTranslation();
  const session = useSession();
  const toast = useToast();
  const adminToast = useAdminToast();
  const [tab, setTab] = useState<'runs' | 'templates'>('runs');
  const [creating, setCreating] = useState(false);

  const canManage = session.has('tasks.checklist.manage');

  const templates = useQuery({
    enabled: canManage,
    queryKey: ['checklists', 'templates'],
    queryFn: async (): Promise<ChecklistTemplate[]> => {
      const { data, error } = await session.api.GET('/api/v1/checklists/templates', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ChecklistTemplateList).items;
    },
  });

  /** Geen `<a href download>`: het token staat in het geheugen (zie `shared/download.ts`). */
  const download = useMutation({
    mutationFn: async () => {
      const { data, error } = await session.api.GET('/api/v1/checklists/runs/export.csv', {
        parseAs: 'blob',
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      await saveBlob(data as unknown as Blob, 'checklists.csv');
    },
    onError: () => toast.show({ tone: 'danger', title: t('errors.generic') }),
  });

  const runs = <RunsPanel />;

  return (
    <AdminPage>
      <PageHead
        title={t('checklistsAdmin.title')}
        sub={t('beheer.werkvloer.checklists.sub')}
        actions={
          <>
            <Btn
              variant="sec"
              icon={<AdminIcon name="download" />}
              disabled={download.isPending}
              onClick={() => download.mutate()}
              testId="runs-export"
            >
              {t('checklistsAdmin.export')}
            </Btn>
            {canManage ? (
              <Btn
                icon={<AdminIcon name="plus" />}
                onClick={() => setCreating(true)}
                testId="template-new"
              >
                {t('checklistsAdmin.addTemplate')}
              </Btn>
            ) : null}
          </>
        }
      />

      {canManage ? (
        <>
          <Tabs
            label={t('checklistsAdmin.title')}
            idPrefix="checklists"
            value={tab}
            onChange={setTab}
            tabs={[
              { key: 'runs', label: t('beheer.werkvloer.checklists.tabRuns') },
              {
                key: 'templates',
                label: t('checklistsAdmin.templates'),
                count: templates.data?.length,
                testId: 'tab-templates',
              },
            ]}
          />
          {tab === 'runs' ? (
            <TabPanel idPrefix="checklists" tabKey="runs">
              {runs}
            </TabPanel>
          ) : (
            <TabPanel idPrefix="checklists" tabKey="templates">
              <TemplatesPanel
                templates={templates}
                onCreate={() => setCreating(true)}
                onDone={adminToast.show}
              />
            </TabPanel>
          )}
        </>
      ) : (
        runs
      )}

      {creating ? (
        <TemplateDrawer
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            setTab('templates');
            adminToast.show(t('beheer.werkvloer.checklists.created'));
          }}
        />
      ) : null}
      {adminToast.element}
    </AdminPage>
  );
}

function RunsPanel() {
  const { t } = useTranslation();
  const { api } = useSession();
  const when = useShortWhen();
  const [status, setStatus] = useState<RunFilter>('active');
  const [period, setPeriod] = useState<Period>('all');
  const [openRun, setOpenRun] = useState<ChecklistRun | null>(null);

  // Alles in één keer (de server geeft er hoogstens 200); de stand "open en te laat" en de tellers
  // rechts komen uit dezelfde lijst. Alleen een expliciete stand gaat naar de server.
  const serverStatus =
    status === 'open' || status === 'overdue' || status === 'completed' ? status : '';

  const runs = useQuery({
    queryKey: ['checklists', 'runs', serverStatus],
    queryFn: async (): Promise<ChecklistRun[]> => {
      const { data, error } = await api.GET('/api/v1/checklists/runs', {
        params: { query: serverStatus ? { status: serverStatus } : {} },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ChecklistRunList).items;
    },
  });

  const all = useQuery({
    queryKey: ['checklists', 'runs', ''],
    queryFn: async (): Promise<ChecklistRun[]> => {
      const { data, error } = await api.GET('/api/v1/checklists/runs', {
        params: { query: {} },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ChecklistRunList).items;
    },
  });

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const weekAgo = now.getTime() - 7 * 24 * 60 * 60 * 1000;
  const inPeriod = (run: ChecklistRun) => {
    const planned = new Date(run.plannedFor).getTime();
    if (period === 'today') return planned >= startOfToday;
    if (period === 'week') return planned >= weekAgo;
    return true;
  };

  const everything = all.data ?? [];
  const overdue = everything.filter((run) => run.status === 'overdue' && inPeriod(run));

  // De rode samenvattingsrij: tientallen te late rondes als één rij, met de oudste en de vaakst
  // gemiste (basis-spec, "23 rondes te laat").
  const oldest = [...overdue].sort((a, b) => a.plannedFor.localeCompare(b.plannedFor))[0];
  const missed = new Map<string, number>();
  for (const run of overdue) missed.set(run.templateName, (missed.get(run.templateName) ?? 0) + 1);
  const mostMissed = [...missed.entries()].sort((a, b) => b[1] - a[1])[0];

  const listed = (runs.data ?? []).filter(
    (run) => inPeriod(run) && (status !== 'active' || run.status === 'open'),
  );
  const loading = status === 'active' ? all : runs;

  const week = everything.filter((run) => new Date(run.plannedFor).getTime() >= weekAgo);

  return (
    <Grid variant="side">
      <Stack>
        <section
          className="a-card"
          data-testid="checklist-runs"
          aria-label={t('beheer.werkvloer.checklists.tabRuns')}
        >
          <Toolbar>
            <FilterSelect
              label={t('checklistsAdmin.filterStatus')}
              value={status}
              neutral="all"
              onChange={(value) => setStatus(value as RunFilter)}
              options={[
                { value: 'active', label: t('beheer.werkvloer.checklists.openAndLate') },
                { value: 'open', label: t('checklists.status.open') },
                { value: 'overdue', label: t('checklists.status.overdue') },
                { value: 'completed', label: t('checklists.status.completed') },
                { value: 'all', label: t('checklistsAdmin.allStatuses') },
              ]}
              testId="runs-filter"
            />
            <FilterSelect
              label={t('beheer.werkvloer.checklists.period')}
              value={period}
              neutral="all"
              onChange={(value) => setPeriod(value as Period)}
              options={[
                { value: 'all', label: t('beheer.werkvloer.checklists.periodAll') },
                { value: 'today', label: t('beheer.werkvloer.checklists.periodToday') },
                { value: 'week', label: t('beheer.werkvloer.checklists.periodWeek') },
              ]}
              testId="runs-period"
            />
          </Toolbar>

          {status === 'active' && overdue.length > 0 && oldest ? (
            <button
              type="button"
              className="a-item wv-row wv-late"
              onClick={() => setStatus('overdue')}
              data-testid="runs-late-summary"
            >
              <span className="a-tile danger" aria-hidden="true">
                <AdminIcon name="clock" size={20} />
              </span>
              <div className="a-item-info">
                <div className="a-name">
                  {t('beheer.werkvloer.checklists.lateCount', { count: overdue.length })}
                </div>
                <div className="a-meta">
                  {t('beheer.werkvloer.checklists.oldest', {
                    name: oldest.templateName,
                    date: formatDate(oldest.plannedFor, {
                      weekday: 'short',
                      day: '2-digit',
                      month: '2-digit',
                    }),
                  })}
                  {mostMissed && mostMissed[1] > 1
                    ? ` ${t('beheer.werkvloer.checklists.mostMissed', {
                        name: mostMissed[0],
                        count: mostMissed[1],
                      })}`
                    : ''}
                </div>
              </div>
              <span className="a-link">{t('beheer.werkvloer.checklists.showThem')}</span>
            </button>
          ) : null}

          {loading.isPending ? (
            <Loading />
          ) : loading.isError ? (
            <LoadError onRetry={() => void loading.refetch()} error={loading.error} />
          ) : listed.length === 0 ? (
            <Empty
              icon={<AdminIcon name="clipboardCheck" size={24} />}
              title={t('checklistsAdmin.noRunsTitle')}
              text={t('checklistsAdmin.noRunsHint')}
            />
          ) : (
            listed.map((run) => (
              <div key={run.id} className="a-item" data-testid="run-row">
                <span
                  className={cn(
                    'a-tile',
                    run.status === 'overdue'
                      ? 'danger'
                      : run.status === 'completed'
                        ? 'green'
                        : 'orange',
                  )}
                  aria-hidden="true"
                >
                  <AdminIcon name="clipboardCheck" size={20} />
                </span>
                <div className="a-item-info">
                  <div className="a-item-top">
                    {run.status === 'overdue' ? (
                      <Badge tone="danger">{t('checklists.status.overdue')}</Badge>
                    ) : run.status === 'completed' ? (
                      <Badge tone="ok">{t('checklists.status.completed')}</Badge>
                    ) : null}
                    {run.deviations > 0 ? (
                      <Badge tone="attention">
                        {t('checklistsAdmin.deviations', { count: run.deviations })}
                      </Badge>
                    ) : null}
                    <span className="a-name">{run.templateName}</span>
                  </div>
                  <div className="a-meta">
                    {run.status === 'completed'
                      ? t('beheer.werkvloer.checklists.doneBy', {
                          name: run.completedByName ?? '—',
                          when: when(run.completedAt),
                        })
                      : t('beheer.werkvloer.checklists.dueAt', {
                          when: when(run.dueAt),
                          count: run.remaining,
                        })}
                  </div>
                  <RunValues run={run} />
                </div>
                <Btn variant="ghost" size="sm" onClick={() => setOpenRun(run)} testId="run-open">
                  {t('beheer.werkvloer.checklists.open')}
                </Btn>
              </div>
            ))
          )}
        </section>
      </Stack>

      <Stack>
        <section className="a-card">
          <CardHead title={t('beheer.werkvloer.checklists.thisWeek')} />
          <CardPad>
            <Stats
              items={[
                {
                  label: t('checklists.status.open'),
                  value: everything.filter((run) => run.status === 'open').length,
                },
                {
                  label: t('checklists.status.overdue'),
                  value: everything.filter((run) => run.status === 'overdue').length,
                },
                {
                  label: t('beheer.werkvloer.checklists.deviation'),
                  value: week.reduce((sum, run) => sum + run.deviations, 0),
                  sub: t('beheer.werkvloer.checklists.thisWeekLower'),
                },
              ]}
            />
          </CardPad>
        </section>
        <section className="a-card">
          <CardPad>
            <Note tone="info">{t('beheer.werkvloer.checklists.boundsNote')}</Note>
          </CardPad>
        </section>
      </Stack>

      {openRun ? <RunDrawer run={openRun} onClose={() => setOpenRun(null)} /> : null}
    </Grid>
  );
}

/** Per item de waarde op één regel: "Noodstoppen: Ja · Koelwater: 14 °C (norm 8–12)". */
function RunValues({ run }: { run: ChecklistRun }) {
  const { t } = useTranslation();
  if (run.items.length === 0) return null;
  return (
    <div className="a-meta" style={{ marginTop: 4 }}>
      {run.items.map((item, index) => {
        const result = run.results.find((candidate) => candidate.itemId === item.id);
        const value = describeValue(item, result, t);
        return (
          <span key={item.id}>
            {index > 0 ? ' · ' : ''}
            {item.label}:{' '}
            {result?.deviation ? (
              <b className="wv-dev">
                {value}
                {norm(item)
                  ? ` (${t('beheer.werkvloer.checklists.norm', { norm: norm(item) })})`
                  : ''}
              </b>
            ) : (
              value || t('checklistsAdmin.notDone')
            )}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Eén ronde in detail: per item de waarde, en wie het wanneer afvinkte. Dat laatste is waarom dit
 * scherm achter een eigen recht staat (ADR-0014).
 */
function RunDrawer({ run, onClose }: { run: ChecklistRun; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Drawer
      open
      onClose={onClose}
      title={run.templateName}
      sub={run.audienceDescription}
      testId="run-drawer"
      footer={
        <Btn variant="sec" onClick={onClose}>
          {t('common.close')}
        </Btn>
      }
    >
      <DefinitionList
        items={[
          [t('checklistsAdmin.filterStatus'), t(`checklists.status.${run.status}`)],
          [t('beheer.werkvloer.checklists.plannedFor'), formatDateTime(run.plannedFor)],
          // De termijn, niet het uur van de planning: dezelfde woorden als in de app.
          [t('beheer.werkvloer.checklists.dueLabel'), formatDateTime(run.dueAt)],
          ...(run.completedAt
            ? ([
                [
                  t('checklists.status.completed'),
                  `${formatDateTime(run.completedAt)}${run.completedByName ? ` · ${run.completedByName}` : ''}`,
                ],
              ] as [string, string][])
            : []),
          [t('beheer.werkvloer.checklists.deviation'), String(run.deviations)],
        ]}
      />
      <Sect title={t('beheer.werkvloer.checklists.items')}>
        <ul className="wv-log">
          {run.items.map((item) => {
            const result = run.results.find((candidate) => candidate.itemId === item.id);
            const value = describeValue(item, result, t);
            return (
              <li key={item.id}>
                <span className="a-muted">{item.label}:</span>{' '}
                {result?.deviation ? (
                  <b className="wv-dev">
                    {value}
                    {norm(item)
                      ? ` (${t('beheer.werkvloer.checklists.norm', { norm: norm(item) })})`
                      : ''}
                  </b>
                ) : (
                  value || t('checklistsAdmin.notDone')
                )}
                {result?.doneByName ? (
                  <span className="a-muted">
                    {' '}
                    {t('checklistsAdmin.by', {
                      name: result.doneByName,
                      when: result.doneAt ? formatDateTime(result.doneAt) : '',
                    })}
                  </span>
                ) : null}
                {result?.comment ? <span> — {result.comment}</span> : null}
              </li>
            );
          })}
        </ul>
      </Sect>
    </Drawer>
  );
}

/** De grenzen van een getal: "8–12" of "≥ 8". */
function norm(item: ChecklistItem): string {
  if (item.type !== 'number' || (item.min === null && item.max === null)) return '';
  if (item.min !== null && item.max !== null) return `${item.min}–${item.max}`;
  return item.min !== null ? `≥ ${item.min}` : `≤ ${item.max}`;
}

/** Eén waarde in mensentaal; dezelfde regels als de CSV-export op de server. */
function describeValue(
  item: ChecklistItem,
  result: ChecklistResult | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (!result) return '';
  if (item.type === 'photo') {
    return result.fileIds.length > 0
      ? t('beheer.werkvloer.checklists.photos', { count: result.fileIds.length })
      : '';
  }
  if (result.value === '') return '';
  if (item.type === 'check') {
    return result.value === 'ja' ? t('appChecklists.yes') : t('appChecklists.no');
  }
  if (item.type === 'number') return item.unit ? `${result.value} ${item.unit}` : result.value;
  return result.value;
}

/** De tab Sjablonen: elke checklist met planning, en wat je ermee kunt. */
function TemplatesPanel({
  templates,
  onCreate,
  onDone,
}: {
  templates: {
    data?: ChecklistTemplate[];
    isPending: boolean;
    isError: boolean;
    error?: unknown;
    refetch: () => unknown;
  };
  onCreate: () => void;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<string | null>(null);
  const editTemplate = (templates.data ?? []).find((template) => template.id === editing) ?? null;

  return (
    <section
      className="a-card"
      data-testid="checklist-templates"
      aria-label={t('checklistsAdmin.templates')}
    >
      {templates.isPending ? (
        <Loading />
      ) : templates.isError ? (
        <LoadError onRetry={() => void templates.refetch()} error={templates.error} />
      ) : (templates.data ?? []).length === 0 ? (
        <Empty
          icon={<AdminIcon name="clipboardList" size={24} />}
          title={t('beheer.werkvloer.checklists.noTemplatesTitle')}
          text={t('checklistsAdmin.templatesIntro')}
          action={
            <Btn icon={<AdminIcon name="plus" />} onClick={onCreate}>
              {t('checklistsAdmin.addTemplate')}
            </Btn>
          }
        />
      ) : (
        (templates.data ?? []).map((template) => (
          <TemplateRow
            key={template.id}
            template={template}
            onEdit={() => setEditing(template.id)}
            onDone={onDone}
          />
        ))
      )}
      {editTemplate ? (
        <ItemsDrawer template={editTemplate} onClose={() => setEditing(null)} onDone={onDone} />
      ) : null}
    </section>
  );
}

function TemplateRow({
  template,
  onEdit,
  onDone,
}: {
  template: ChecklistTemplate;
  onEdit: () => void;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['checklists'] });

  const startNow = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/checklists/runs', {
        body: { templateId: template.id },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void invalidate();
      onDone(t('checklistsAdmin.started'));
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  const toggle = useMutation({
    mutationFn: async () => {
      const { error } = await api.PATCH('/api/v1/checklists/templates/{templateId}', {
        params: { path: { templateId: template.id } },
        body: { active: !template.active },
      });
      if (error) throw error;
    },
    onSuccess: () => void invalidate(),
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  return (
    <div className="a-item" data-testid="template-card">
      <span className="a-tile" aria-hidden="true">
        <AdminIcon name="clipboardList" size={20} />
      </span>
      <div className="a-item-info">
        <div className="a-item-top">
          <span className="a-name">{template.name}</span>
          {!template.active ? <Badge>{t('checklistsAdmin.inactive')}</Badge> : null}
          {template.openRuns > 0 ? (
            <Badge tone="attention">
              {t('checklistsAdmin.openRuns', { count: template.openRuns })}
            </Badge>
          ) : null}
        </div>
        <div className="a-meta">
          {template.audienceDescription}
          {template.assigneeName
            ? ` + ${t('checklistsAdmin.assignedTo', { name: template.assigneeName })}`
            : ''}{' '}
          ·{' '}
          {template.schedule === 'manual'
            ? t('checklistsAdmin.scheduleOption.manual')
            : t('checklistsAdmin.scheduleSummary', {
                schedule: t(`checklistsAdmin.scheduleOption.${template.schedule}`),
                time: template.planTime,
                hours: template.dueHours,
              })}
        </div>
        <div className="a-meta">
          {template.items.length === 0
            ? t('checklistsAdmin.noItems')
            : template.items.map((item) => item.label).join(' · ')}
        </div>
      </div>
      <div className="a-actions" style={{ flexWrap: 'nowrap' }}>
        <IconBtn
          label={t('common.edit')}
          icon={<AdminIcon name="pencil" />}
          onClick={onEdit}
          testId="template-edit"
        />
        <Btn
          variant="ghost"
          size="sm"
          disabled={template.items.length === 0 || startNow.isPending}
          onClick={() => startNow.mutate()}
          testId="template-start"
        >
          {t('checklistsAdmin.startNow')}
        </Btn>
        <Btn
          variant="ghost"
          size="sm"
          disabled={toggle.isPending}
          onClick={() => toggle.mutate()}
          testId="template-toggle"
        >
          {template.active ? t('checklistsAdmin.disable') : t('checklistsAdmin.enable')}
        </Btn>
      </div>
    </div>
  );
}

/** De items van een bestaande checklist: weghalen en toevoegen, zoals voorheen per kaart. */
function ItemsDrawer({
  template,
  onClose,
  onDone,
}: {
  template: ChecklistTemplate;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<DraftItem>(emptyItem());
  // De bewaarde planning, en niet de standaard van een nieuwe checklist (hertest 29 okt.).
  const [planning, setPlanning] = useState<PlanningDraft>(() => planningOf(template));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['checklists'] });

  /*
    De planning wijzigen na het aanmaken (hertest 29 okt.). `PATCH /checklists/templates/:id`
    kon het al en zet ook de planning in `scheduling` gelijk; alleen dit scherm ontbrak.
  */
  const savePlanning = useMutation({
    mutationFn: async () => {
      const { error } = await api.PATCH('/api/v1/checklists/templates/{templateId}', {
        params: { path: { templateId: template.id } },
        body: planningBody(planning),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void invalidate();
      onDone(t('checklistsAdmin.planningSaved'));
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  const addItem = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/checklists/templates/{templateId}/items', {
        params: { path: { templateId: template.id } },
        body: itemBody(draft),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void invalidate();
      setDraft(emptyItem());
      onDone(t('checklistsAdmin.itemAdded'));
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  const removeItem = useMutation({
    mutationFn: async (itemId: string) => {
      const { error } = await api.DELETE(
        '/api/v1/checklists/templates/{templateId}/items/{itemId}',
        { params: { path: { templateId: template.id, itemId } } },
      );
      if (error) throw error;
    },
    onSuccess: () => void invalidate(),
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title={template.name}
      sub={template.audienceDescription}
      testId="template-items-drawer"
      footer={<Btn onClick={onClose}>{t('beheer.werkvloer.done')}</Btn>}
    >
      <Sect title={t('beheer.werkvloer.checklists.items')}>
        {template.items.length === 0 ? (
          <p className="a-muted">{t('checklistsAdmin.noItems')}</p>
        ) : (
          template.items.map((item) => (
            <div
              key={item.id}
              className="a-item"
              style={{ padding: '10px 0' }}
              data-testid="template-item"
            >
              <div className="a-item-info">
                <div className="a-name">{item.label}</div>
                <div className="a-meta">
                  {t(`beheer.werkvloer.checklists.type.${item.type}`)}
                  {norm(item) ? ` · ${norm(item)} ${item.unit}` : ''}
                  {item.required ? '' : ` · ${t('checklistsAdmin.optional')}`}
                </div>
              </div>
              <IconBtn
                label={t('beheer.werkvloer.checklists.removeItem')}
                icon={<AdminIcon name="trash" />}
                disabled={removeItem.isPending}
                onClick={() => removeItem.mutate(item.id)}
                testId="template-item-remove"
              />
            </div>
          ))
        )}
      </Sect>
      <Sect title={t('checklistsAdmin.addItem')}>
        <ItemEditor item={draft} onChange={setDraft} />
        <div>
          <Btn
            variant="sec"
            size="sm"
            icon={<AdminIcon name="plus" />}
            disabled={draft.label.trim() === '' || addItem.isPending}
            onClick={() => addItem.mutate()}
            testId="item-add"
          >
            {t('checklistsAdmin.addItem')}
          </Btn>
        </div>
      </Sect>
      <Sect title={t('checklistsAdmin.schedule')}>
        <PlanningFields value={planning} onChange={setPlanning} />
        <div>
          <Btn
            variant="sec"
            size="sm"
            disabled={!planningChanged(template, planning) || savePlanning.isPending}
            onClick={() => savePlanning.mutate()}
            testId="template-planning-save"
          >
            {t('checklistsAdmin.savePlanning')}
          </Btn>
        </div>
      </Sect>
    </Drawer>
  );
}

/** Eén item: wat moet er gebeuren, het type, en bij een getal de eenheid en de grenzen. */
function ItemEditor({ item, onChange }: { item: DraftItem; onChange: (item: DraftItem) => void }) {
  const { t } = useTranslation();
  return (
    <div className="a-form" style={{ gap: 8 }}>
      <Row>
        <Input
          aria-label={t('checklistsAdmin.itemLabel')}
          placeholder={t('checklistsAdmin.itemLabel')}
          value={item.label}
          maxLength={200}
          onChange={(event) => onChange({ ...item, label: event.target.value })}
          data-testid="item-label"
        />
        <Select
          aria-label={t('beheer.werkvloer.checklists.typeLabel')}
          value={item.type}
          onChange={(event) => onChange({ ...item, type: event.target.value as ChecklistItemType })}
          data-testid="item-type"
        >
          {ITEM_TYPES.map((value) => (
            <option key={value} value={value}>
              {t(`beheer.werkvloer.checklists.type.${value}`)}
            </option>
          ))}
        </Select>
      </Row>
      {item.type === 'number' ? (
        <Row three>
          <Input
            aria-label={t('checklistsAdmin.unit')}
            placeholder={t('checklistsAdmin.unit')}
            value={item.unit}
            maxLength={20}
            onChange={(event) => onChange({ ...item, unit: event.target.value })}
            data-testid="item-unit"
          />
          <Input
            aria-label={t('beheer.werkvloer.checklists.min')}
            placeholder={t('beheer.werkvloer.checklists.min')}
            inputMode="decimal"
            value={item.min}
            onChange={(event) => onChange({ ...item, min: event.target.value })}
            data-testid="item-min"
          />
          <Input
            aria-label={t('beheer.werkvloer.checklists.max')}
            placeholder={t('beheer.werkvloer.checklists.max')}
            inputMode="decimal"
            value={item.max}
            onChange={(event) => onChange({ ...item, max: event.target.value })}
            data-testid="item-max"
          />
        </Row>
      ) : null}
      {/* Zoals bij een formulierveld (hertest 29 okt.): standaard verplicht, uit te zetten. */}
      <Checkbox
        checked={item.required}
        onChange={(required) => onChange({ ...item, required })}
        label={t('checklistsAdmin.required')}
        hint={t('checklistsAdmin.requiredHint')}
        testId="item-required"
      />
    </div>
  );
}

/**
 * De planning van een checklist: wanneer ze klaarstaat en hoe lang de ploeg heeft. Dezelfde velden
 * bij toevoegen en bij bewerken, zodat "Bewerken" niets kan wat "Toevoegen" niet kon en omgekeerd.
 */
function PlanningFields({
  value,
  onChange,
}: {
  value: PlanningDraft;
  onChange: (next: PlanningDraft) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Row three>
        <Field label={t('beheer.werkvloer.checklists.schedule')}>
          {(props) => (
            <Select
              {...props}
              value={value.schedule}
              onChange={(event) =>
                onChange({ ...value, schedule: event.target.value as ChecklistSchedule })
              }
              data-testid="template-schedule"
            >
              {SCHEDULES.map((option) => (
                <option key={option} value={option}>
                  {t(`checklistsAdmin.scheduleOption.${option}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {value.schedule !== 'manual' ? (
          <Field label={t('checklistsAdmin.planTime')}>
            {(props) => (
              <Input
                {...props}
                type="time"
                value={value.planTime}
                onChange={(event) => onChange({ ...value, planTime: event.target.value })}
                data-testid="template-plan-time"
              />
            )}
          </Field>
        ) : (
          <div />
        )}
        <Field label={t('beheer.werkvloer.checklists.dueHours')}>
          {(props) => (
            <Input
              {...props}
              inputMode="numeric"
              value={value.dueHours}
              onChange={(event) => onChange({ ...value, dueHours: event.target.value })}
              data-testid="template-due-hours"
            />
          )}
        </Field>
      </Row>
      {value.schedule === 'weekly' ? (
        <Field label={t('checklistsAdmin.weekday')}>
          {(props) => (
            <Select
              {...props}
              value={value.weekday}
              onChange={(event) => onChange({ ...value, weekday: event.target.value })}
              data-testid="template-weekday"
            >
              {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                <option key={day} value={String(day)}>
                  {t(`checklistsAdmin.day.${day}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      ) : null}
    </>
  );
}

/**
 * Checklist toevoegen (W5): naam, items, doelgroep, toewijzing en planning in één paneel.
 *
 * De API maakt eerst de checklist en dan elk item apart. Lukt een item niet, dan blijft het paneel
 * open en onthoudt het wat al bewaard is: nog eens op Opslaan drukken maakt geen tweede checklist.
 */
function TemplateDrawer({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [items, setItems] = useState<DraftItem[]>(() => [emptyItem()]);
  const [audience, setAudience] = useState<AudienceSpec>({ type: 'all' });
  // Naast de doelgroep: één medewerker die de checklist hoe dan ook krijgt (Extra 6, punt 9).
  const [assignee, setAssignee] = useState<{ id: string; name: string } | null>(null);
  const [planning, setPlanning] = useState<PlanningDraft>(NEW_PLANNING);
  const [saved, setSaved] = useState<{ templateId: string; items: number } | null>(null);

  const filled = items.filter((item) => item.label.trim() !== '');

  const create = useMutation({
    mutationFn: async () => {
      let progress = saved;
      if (!progress) {
        const { data, error } = await api.POST('/api/v1/checklists/templates', {
          body: {
            name,
            description: '',
            audience,
            assigneeId: assignee?.id ?? null,
            ...planningBody(planning),
          },
        });
        if (error || !data) throw error ?? new Error('Mislukt');
        progress = { templateId: (data as ChecklistTemplate).id, items: 0 };
        setSaved(progress);
      } else {
        /*
          Een tweede poging na een mislukt item: de checklist bestaat al. Wat de beheerder intussen
          aan doelgroep, toewijzing of planning veranderde, ging toen verloren en bleef op de eerste
          keuze staan (hertest 29 okt.). Nu gaat het mee.
        */
        const { error } = await api.PATCH('/api/v1/checklists/templates/{templateId}', {
          params: { path: { templateId: progress.templateId } },
          body: { audience, assigneeId: assignee?.id ?? null, ...planningBody(planning) },
        });
        if (error) throw error;
      }
      for (let index = progress.items; index < filled.length; index += 1) {
        const item = filled[index] as DraftItem;
        const { error } = await api.POST('/api/v1/checklists/templates/{templateId}/items', {
          params: { path: { templateId: progress.templateId } },
          body: itemBody(item),
        });
        if (error) throw error;
        progress = { ...progress, items: index + 1 };
        setSaved(progress);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['checklists'] });
      onSaved();
    },
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: ['checklists'] });
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') });
    },
  });

  const update = (key: number, next: DraftItem) =>
    setItems((current) => current.map((item) => (item.key === key ? next : item)));

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('checklistsAdmin.addTemplate')}
      testId="template-drawer"
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            disabled={name.trim() === '' || audienceIncomplete(audience) || create.isPending}
            onClick={() => create.mutate()}
            testId="template-create"
          >
            {t('beheer.werkvloer.checklists.save')}
          </Btn>
        </>
      }
    >
      <Field label={t('checklistsAdmin.templateName')}>
        {(props) => (
          <Input
            {...props}
            value={name}
            maxLength={120}
            readOnly={saved !== null}
            onChange={(event) => setName(event.target.value)}
            data-testid="template-name"
          />
        )}
      </Field>

      <Sect title={t('beheer.werkvloer.checklists.items')}>
        {items.map((item, index) => (
          <div
            key={item.key}
            className="a-item"
            style={{ padding: '10px 0' }}
            data-testid="draft-item"
          >
            <div className="a-item-info">
              <ItemEditor item={item} onChange={(next) => update(item.key, next)} />
            </div>
            <IconBtn
              label={t('beheer.werkvloer.checklists.removeItem')}
              icon={<AdminIcon name="trash" />}
              disabled={saved !== null && index < saved.items}
              onClick={() =>
                setItems((current) =>
                  current.length > 1
                    ? current.filter((other) => other.key !== item.key)
                    : [emptyItem()],
                )
              }
              testId="draft-item-remove"
            />
          </div>
        ))}
        <div>
          <Btn
            variant="sec"
            size="sm"
            icon={<AdminIcon name="plus" />}
            onClick={() => setItems((current) => [...current, emptyItem()])}
            testId="item-add"
          >
            {t('checklistsAdmin.addItem')}
          </Btn>
        </div>
      </Sect>

      <AudiencePicker value={audience} onChange={setAudience} onPreview={() => undefined} />
      <p className="a-hint">{t('beheer.werkvloer.checklists.audienceHint')}</p>

      <PersonPicker value={assignee} onChange={setAssignee} />

      <PlanningFields value={planning} onChange={setPlanning} />
    </Drawer>
  );
}

/**
 * Eén medewerker naast de doelgroep (Extra 6, punt 9).
 *
 * "De opstartronde van lijn 3, en Kevin doet de weekcontrole" is twee dingen tegelijk; daarom
 * staat dit naast de doelgroepkiezer en niet erin. Een doelgroep beschrijft een groep mensen, een
 * toewijzing is een opdracht aan één mens - en dat verschil hoort zichtbaar te blijven.
 *
 * Zoeken en geen keuzelijst, net als bij het toewijzen van een melding: bij een paar duizend
 * medewerkers is een `<select>` onwerkbaar.
 */
function PersonPicker({
  value,
  onChange,
}: {
  value: { id: string; name: string } | null;
  onChange: (person: { id: string; name: string } | null) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const [search, setSearch] = useState('');

  const candidates = useQuery({
    enabled: search.trim().length >= 2,
    queryKey: ['checklists', 'assignee', search],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/people', {
        params: { query: { search: search.trim(), limit: '8', status: 'active' } } as never,
      });
      return (data as PersonPage | undefined)?.items ?? [];
    },
  });

  return (
    <div className="a-field" data-testid="template-assignee-picker">
      <div className="a-label">
        {t('beheer.werkvloer.checklists.assignee')}{' '}
        <span className="a-opt">{t('beheer.kit.optional')}</span>
      </div>
      {value ? (
        <div className="a-inline" data-testid="template-assignee">
          <span>{t('checklistsAdmin.assignedTo', { name: value.name })}</span>
          <Btn
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
            testId="template-assignee-clear"
          >
            {t('checklistsAdmin.assigneeClear')}
          </Btn>
        </div>
      ) : (
        <div>
          <SearchField
            label={t('beheer.werkvloer.checklists.assignee')}
            placeholder={t('beheer.werkvloer.checklists.assigneePlaceholder')}
            value={search}
            onChange={setSearch}
            testId="template-assignee-search"
          />
          {candidates.data && candidates.data.length > 0 ? (
            <ul className="wv-options">
              {candidates.data.map((person) => (
                <li key={person.id}>
                  <button
                    type="button"
                    data-testid="template-assignee-option"
                    onClick={() => {
                      onChange({
                        id: person.id,
                        name: `${person.firstName} ${person.lastName}`.trim(),
                      });
                      setSearch('');
                    }}
                  >
                    {person.firstName} {person.lastName}
                    {person.employeeNumber ? (
                      <span className="a-muted"> · {person.employeeNumber}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
      <div className="a-hint">{t('checklistsAdmin.assigneeHint')}</div>
    </div>
  );
}
